<template>
  <div class="post-webwidget-v2-playlist" :class="playlistClassList">
    <div class="post-webwidget-v2-playlist-row post-webwidget-v2-playlist-row--heading">
      <h4 class="post-webwidget-v2-playlist-heading-label">Playlist</h4>
    </div>
    <div v-if="isLoading || errorMessage" class="post-webwidget-v2-playlist-row">
      <template v-if="isLoading">
        Loading episodes...
      </template>
      <template v-if="errorMessage">
        {{ errorMessage }}
      </template>
    </div>
    <template v-else>
      <div v-if="episodeList.length === 0" class="post-webwidget-v2-playlist-row">
        No episodes exist.
      </div>
      <div v-else class="post-webwidget-v2-playlist-episode-list-container">
        <!-- D-233 (A9): THE ROW IS THE BUTTON, and the two icons stop being controls.
             🪤 This is the one conversion in the series that could not be a tag swap per
             offender, because the row was clickable AND held two clickable children.
             Nested interactive content is invalid, so three `@click`s had to become ONE
             control — and the plan left which one open.

             🔑 The row wins, because the row is what a mouse user already aims at: the
             10px icon is a 10px target inside a full-width strip that has had
             `cursor: pointer` since v2 shipped. Promoting the icon and demoting the row
             would have taken the big target away to keep the small one.

             🔑 AND THE THREE HANDLERS WERE ALREADY THE SAME HANDLER. Measured against
             `V2Player`: for every reachable state of (`activeEpisodeId`, clicked id),
             `episode-click` and `episode-play-button` produce an identical transition —
             `setActiveEpisode` when the ids differ or none is set, `play()` when they
             match. So collapsing them loses no behaviour; there is a leg per state.

             ⚠️ ONE DELIBERATE BEHAVIOUR CHANGE, and it makes the row honest. Clicking a
             PLAYING row outside the icon used to call `play()` on already-playing audio
             — a no-op — while the icon it was rendering said "pause". The row now does
             what it depicts. That is a visible change for mouse users and it has its own
             leg; it is not a side effect of the conversion. -->
        <button
          v-for="episode in episodeList" :key="episode.id"
          type="button"
          class="post-webwidget-v2-playlist-row post-webwidget-v2-playlist-episode-list-item"
          :class="getEpisodeRowClassList(episode.id)"
          :aria-label="episodeActionLabel(episode)"
          @click="onEpisodeClick(episode.id)"
        >
          <!-- Phrasing content only: the row is a <button>, which may contain phrasing
               content, not block <div>s (the UA does not auto-close a button on a nested
               div the way it would on a <p>). These wrappers set their own `display` via
               their classes — the container is `inline-flex`, the name/date are flex
               items — so the <span> forms render identically. -->
          <span class="post-webwidget-v2-playlist-episode-list-item-contents">
            <!-- D-255 (A9): `alt=""`, not a better string. These were the ENTIRE visible
                 content of their controls, so before the conversion above they were a
                 button-shaped thing with no accessible name at all. Now the name is on
                 the row and the glyph is decoration that duplicates it — an `alt` here
                 would make AT say the action twice. -->
            <span
              v-if="isEpisodePlaying(episode.id)"
              class="post-webwidget-v2-playlist-episode-list-item-contents-pause-button"
            >
              <img :src="pauseButtonImageSrc" width="10" height="10" alt="">
            </span>
            <span
              v-else
              class="post-webwidget-v2-playlist-episode-list-item-contents-play-button"
            >
              <img :src="playButtonImageSrc" width="10" height="10" alt="">
            </span>
            <span class="post-webwidget-v2-playlist-episode-list-item-contents-episode-name">
              {{ episode.name }}
            </span>
            <!-- 🔑 One branch, not two. The `aria-label` above has to state the same date
                 this renders, and the way that pair drifts apart is by being written
                 twice — D-238 is exactly that defect in v3's pagination. Both now read
                 `episodeDate`. -->
            <span class="post-webwidget-v2-playlist-episode-list-item-contents-episode-date">
              {{ episodeDate(episode) }}
            </span>
          </span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
// Vue-1 `require('...svg')` -> ES-module imports so Vite resolves the assets
// (mirrors the v3 subtree + LiveAudioPlayer). Small SVGs inline as data URIs.
import playWhiteSvg from '../../static/icons/white/svg/play.svg';
import playGreySvg from '../../static/icons/grey/svg/play.svg';
import pauseWhiteSvg from '../../static/icons/white/svg/pause.svg';
import pauseGreySvg from '../../static/icons/grey/svg/pause.svg';

export default {
  name: 'playlist',
  // Vue3 (LP-4/MR-78, the LP-2-deferred emits item): declare the $emitted events
  // so parent listeners bind as component events, not native-attr fallthrough.
  // D-233 (A9): `episode-play-button` is GONE, not merely unused. The row conversion
  // above collapsed three controls into one, and this event was the second of two
  // handlers that had already been measured to do the same thing — so leaving it
  // declared would advertise a channel nothing can emit. A leg pins its absence, the
  // way A7's pins `nextAutoplayAction`.
  emits: ['episode-click', 'episode-pause-button'],
  props: {
    darkTheme: Boolean,
    errorMessage: String,
    isLoading: Boolean,
    isActiveEpisodePaused: {
      type: Boolean,
      default: true,
    },
    episodeList: {
      required: true,
      type: Array,
    },
    activeEpisodeId: {
      required: false,
      type: Number,
      default: undefined,
    },
  },
  computed: {
    playlistClassList() {
      return {
        'post-webwidget-v2-playlist--dark-theme': this.darkTheme,
      };
    },
    pauseButtonImageSrc() {
      return this.darkTheme ? pauseWhiteSvg : pauseGreySvg;
    },
    playButtonImageSrc() {
      return this.darkTheme ? playWhiteSvg : playGreySvg;
    },
  },
  methods: {
    getEpisodeRowClassList(episodeId) {
      return {
        'post-webwidget-v2-playlist-episode-list-item--active': episodeId === this.activeEpisodeId,
      };
    },
    onPauseButtonClick() {
      this.$emit('episode-pause-button');
    },

    isEpisodePlaying(episodeId) {
      return episodeId === this.activeEpisodeId && !this.isActiveEpisodePaused;
    },
    formatDate(date) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },

    /**
     * The date this row DISPLAYS. Read by the template and by `episodeActionLabel`, so
     * the announced date cannot drift from the rendered one.
     *
     * 🪤 `widget_publish_date || timestamp_created`, preserving the template's original
     * `v-if` exactly: `widget_publish_date` is `null` (not absent) when the feed omits
     * it, which is falsy, and `V2Player.processShowData` is where that null is set.
     */
    episodeDate(episode) {
      return this.formatDate(episode.widget_publish_date || episode.timestamp_created);
    },

    /**
     * D-233 (A9). The row button's accessible name: what it will DO, then what it is.
     *
     * 🔑 An `aria-label` REPLACES the subtree, so it has to carry the date as well —
     * otherwise the conversion would silently take the date away from the only users
     * who cannot see it. Announced and rendered come from `episodeDate` together.
     *
     * 🪤 No `aria-pressed`. A7 decided this for `PlayerInfo`'s autoplay toggle and the
     * reasoning transfers unchanged: `aria-pressed` suits a sticky state, and
     * "Pause …, pressed" is unresolvable for a control whose name is already an action.
     */
    episodeActionLabel(episode) {
      const action = this.isEpisodePlaying(episode.id) ? 'Pause' : 'Play';
      // `episode.name || 'episode'` mirrors the now-playing guards (V2Player and v4
      // Player), so a feed that omits a name announces "Play episode, <date>" rather
      // than "Play undefined, <date>".
      return `${action} ${episode.name || 'episode'}, ${this.episodeDate(episode)}`;
    },

    /**
     * D-233 (A9). One control, so one handler — and it dispatches on what the row is
     * currently RENDERING rather than on which element was clicked, because there is
     * only one element now.
     */
    onEpisodeClick(episodeId) {
      if (this.isEpisodePlaying(episodeId)) {
        // The ⚠️ in the template: the row shows a pause glyph in this state, and used to
        // answer a click on it with `play()`.
        this.onPauseButtonClick();
        return;
      }
      this.$emit('episode-click', episodeId);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/variables-v2';
.post-webwidget-v2-playlist {
  &-row {
    padding: 1em;
    text-size-adjust: 100%; // iOS text size fix
    
    &--heading {
      border-top: 1.5px solid $borderColor;
      // border-bottom: 3.5px double $borderColor; // <-- alternative border style to play with
      border-bottom: 1.5px solid $borderColor;
      margin-bottom: 0.5px;
      .post-webwidget-v2-playlist-heading-label {
        margin: 0;
        text-transform: uppercase;
      }
    }
  }

  &-episode-list-container {
    max-height: 165px; // Enough to hold 3.5 items
    overflow: auto;
  }

  &-episode-list-item {
    cursor: pointer;

    // D-233 (A9): the UA reset for the row's promotion to `<button>`.
    //
    // 🔴 SPECIFICITY IS THE WHOLE DESIGN HERE, and A8b got it wrong the first time in
    // v1's pagination: a reset written as `.container button` is (0,1,1) and OUTSPECIFIES
    // the (0,1,0) class that styles the same element, so it silently strips the look it
    // was only supposed to normalise. Everything below is on the bare class at (0,1,0)
    // and is DECLARED FIRST, so:
    //   * `&:not(:first-child)`'s `border-top` (0,2,0) still beats `border: 0`;
    //   * `&:hover` (0,2,0) still beats `background: none`;
    //   * `&--active` (0,1,0) ties and wins on SOURCE ORDER, which is why this block
    //     sits at the top of the parent rather than after it.
    // There is a mutant for each of those three.
    //
    // 🔑 `font: inherit` is not cosmetic. The `font` shorthand carries `line-height`, and
    // a UA button's 13.333px Arial would have resized the episode name and date in all
    // 36 v2 cells — the single largest golden risk in this MR.
    //
    // 🔑 `width: 100%` WITH `box-sizing: border-box` rather than relying on a block
    // button filling its container: v2 has no global `box-sizing` reset (v1's three
    // `* { box-sizing: border-box }` blocks are scoped to v1 components), so a button's
    // UA border-box and a div's content-box had to be reconciled explicitly. The div was
    // `width: auto` + `padding: 1em` inside the container; this is the same used width.
    appearance: none;
    -webkit-appearance: none;
    display: block;
    width: 100%;
    box-sizing: border-box;
    margin: 0;
    border: 0;
    border-radius: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;

    &:not(:first-child) {
      border-top: 1.25px solid $borderColor;
    }
    &:last-child {
      border-bottom-left-radius: $borderRadius;
      border-bottom-right-radius: $borderRadius;
    }

    &:hover {
      background: mix(white, $playlistEpisodeBackground, 1%);
    }

    &--active {
      background: $playlistEpisodeBackground;
      &:hover {
        background: $playlistEpisodeBackground;
      }
    }

    &-contents {
      display: inline-flex;
      width: 100%;

      &-play-button, &-pause-button {
        width: 10px;
        margin-right: 1em;
      }

      &-episode-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-right: 1em;
      }

      &-episode-date {
        flex-grow: 1;
        text-align: right;
        white-space: nowrap;
        color: $playlistEpisodeDateColor;
      }
    }
  }
}

/** Dark theme **/
.post-webwidget-v2-playlist--dark-theme {
  .post-webwidget-v2-playlist-row {
    &--heading {
      border-top-color: $darkThemeBorderColor;
      border-bottom-color: $darkThemeBorderColor;
    }
  }

  .post-webwidget-v2-playlist-episode-list-item { 
    &:not(:first-child) {
      border-color: $darkThemeBorderColor;
    }

    &:hover {
      background: mix(white, $darkThemePlaylistEpisodeBackground, 1%);
    }
  }

  .post-webwidget-v2-playlist-episode-list-item--active {
    background: $darkThemePlaylistEpisodeBackground;
    &:hover {
      background: $darkThemePlaylistEpisodeBackground;
    }
  }

  .post-webwidget-v2-playlist-episode-list-item-contents-episode-date {
    color: $darkThemePlaylistEpisodeDateColor;
  }
}
</style>
